import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer or current submenu when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    // Find the deepest open submenu and close it, or close the drawer
    /** @type {NodeListOf<HTMLElement>} */
    const openSubmenus = this.querySelectorAll('.menu-drawer__submenu.menu-open');
    if (openSubmenus.length > 0) {
      // Close the last (deepest) open submenu
      const deepestSubmenu = openSubmenus[openSubmenus.length - 1];
      if (deepestSubmenu) {
        this.#closeSubmenuElement(deepestSubmenu);
      }
    } else {
      this.#closeDrawer();
    }
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Toggle the main menu drawer
   * @param {Event} [event]
   */
  toggle(event) {
    // Prevent native details toggle - we manage state ourselves
    event?.preventDefault();

    // Check if drawer is currently open (has menu-open class, not just open attribute)
    const isCurrentlyOpen = this.refs.details.classList.contains('menu-open');

    return isCurrentlyOpen ? this.close() : this.open();
  }

  /**
   * Open the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.refs.details;
    const summary = details.querySelector('summary');

    if (!summary) return;

    // Prevent native details toggle - we manage state ourselves
    event?.preventDefault();

    // If already open with menu-open class, don't re-open
    if (details.classList.contains('menu-open')) return;

    // Set the open attribute since we prevent native toggle
    details.setAttribute('open', '');
    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    
    // Lock page scrolling
    document.documentElement.setAttribute('scroll-lock', '');
    
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      setTimeout(() => {
        trapFocus(details);
      }, 0);
    });
  }

  /**
   * Open a submenu by finding the target submenu via aria-controls on the trigger button
   * @param {Event} [event]
   */
  openSubmenu(event) {
    if (!(event?.target instanceof Element)) return;

    /** @type {HTMLButtonElement | null} */
    const button = event.target.closest('button[aria-controls]');
    if (!button) return;

    const submenuId = button.getAttribute('aria-controls');
    if (!submenuId) return;

    /** @type {HTMLElement | null} */
    const submenu = this.querySelector(`#${submenuId}`);
    if (!submenu) return;

    // Update trigger button state
    button.setAttribute('aria-expanded', 'true');

    // Show the submenu
    requestAnimationFrame(() => {
      submenu.classList.add('menu-open');

      setTimeout(() => {
        trapFocus(submenu);
      }, 0);
    });
  }

  /**
   * Close the current submenu (called from back button)
   * @param {Event} [event]
   */
  closeSubmenu(event) {
    if (!(event?.target instanceof Element)) return;

    // Find the submenu that contains this back button
    /** @type {HTMLElement | null} */
    const submenu = event.target.closest('.menu-drawer__submenu');
    if (!submenu) return;

    this.#closeSubmenuElement(submenu);
  }

  /**
   * Close a specific submenu element
   * @param {HTMLElement} submenu
   */
  #closeSubmenuElement(submenu) {
    // Find the trigger button that opened this submenu
    const submenuId = submenu.id;
    /** @type {HTMLButtonElement | null} */
    const triggerButton = this.querySelector(`button[aria-controls="${submenuId}"]`);

    if (triggerButton) {
      triggerButton.setAttribute('aria-expanded', 'false');
    }

    // Hide the submenu
    submenu.classList.remove('menu-open');

    onAnimationEnd(submenu, () => {
      // Return focus to the appropriate element
      if (triggerButton) {
        triggerButton.focus();
      } else {
        trapFocus(this.refs.details);
      }
    });
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#closeDrawer();
  }

  /**
   * Close the main drawer and all submenus
   */
  #closeDrawer() {
    const details = this.refs.details;
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    // Close all open submenus
    const openSubmenus = this.querySelectorAll('.menu-drawer__submenu.menu-open');
    openSubmenus.forEach((submenu) => {
      submenu.classList.remove('menu-open');
      // Reset trigger buttons
      const submenuId = submenu.id;
      const triggerButton = this.querySelector(`button[aria-controls="${submenuId}"]`);
      if (triggerButton) {
        triggerButton.setAttribute('aria-expanded', 'false');
      }
    });

    onAnimationEnd(details, () => {
      details.removeAttribute('open');
      removeTrapFocus();
      
      // Unlock page scrolling
      document.documentElement.removeAttribute('scroll-lock');
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}
